/**
 * Document template lookup — maps template IDs to their configs.
 */
import { ahcQuestionnaire } from '@/frontend/features/printing/documents/ahc-questionnaire.form'
import { amaForm } from '@/frontend/features/printing/documents/ama-form.form'
import { castrateConsent } from '@/frontend/features/printing/documents/consent/castrate-consent'
import { dentalConsentStage1 } from '@/frontend/features/printing/documents/consent/dental-consent-stage1'
import { dentalConsentStage2 } from '@/frontend/features/printing/documents/consent/dental-consent-stage2'
import { hospitalisationConsent } from '@/frontend/features/printing/documents/consent/hospitalisation-consent'
import { sedationConsent } from '@/frontend/features/printing/documents/consent/sedation-consent'
import { spayConsent } from '@/frontend/features/printing/documents/consent/spay-consent'
import { surgeryConsent } from '@/frontend/features/printing/documents/consent/surgery-consent'
import { dentalDischargeStage1 } from '@/frontend/features/printing/documents/dental-discharge-stage1.form'
import { dentalDischargeStage2 } from '@/frontend/features/printing/documents/dental-discharge-stage2.form'
import { neuteringDischarge } from '@/frontend/features/printing/documents/discharge/neutering-discharge'
import { surgeryDischarge } from '@/frontend/features/printing/documents/discharge/surgery-discharge'
import { euthanasiaConsent } from '@/frontend/features/printing/documents/euthanasia-consent.form'
import { fitToFly } from '@/frontend/features/printing/documents/fit-to-fly.form'
import { offLicenceConsent } from '@/frontend/features/printing/documents/off-licence-consent.form'
import { preDentalLetter } from '@/frontend/features/printing/documents/pre-dental-letter.form'
import { preNeuterLetter } from '@/frontend/features/printing/documents/pre-neuter-letter.form'
import { preSedationLetter } from '@/frontend/features/printing/documents/pre-sedation-letter.form'
import { rabiesVaccinationCertificate } from '@/frontend/features/printing/documents/rabies-vaccination-certificate.form'
import { vaccinationCertificate } from '@/frontend/features/printing/documents/vaccination-certificate.form'
import { withBackendDocument } from '@/frontend/features/printing/documents/with-backend-document'

const vaccinationRecord = withBackendDocument({
  id: 'vaccination-record',
  label: 'Vaccination Record',
  request: (patient) => ({
    documentType: 'vaccination-record',
    input: { patientId: patient.id },
  }),
})

const documents = {
  'ahc-questionnaire': ahcQuestionnaire,
  'ama-form': amaForm,
  'castrate-consent': castrateConsent,
  'spay-consent': spayConsent,
  'dental-consent-stage1': dentalConsentStage1,
  'dental-consent-stage2': dentalConsentStage2,
  'dental-discharge-stage1': dentalDischargeStage1,
  'dental-discharge-stage2': dentalDischargeStage2,
  'euthanasia-consent': euthanasiaConsent,
  'fit-to-fly': fitToFly,
  'hospitalisation-consent': hospitalisationConsent,
  'neutering-discharge': neuteringDischarge,
  'off-licence-consent': offLicenceConsent,
  'pre-dental-letter': preDentalLetter,
  'pre-neuter-letter': preNeuterLetter,
  'pre-sedation-letter': preSedationLetter,
  'sedation-consent': sedationConsent,
  'surgery-consent': surgeryConsent,
  'surgery-discharge': surgeryDischarge,
  'rabies-vaccination-certificate': rabiesVaccinationCertificate,
  'vaccination-certificate': vaccinationCertificate,
  'vaccination-record': vaccinationRecord,
} as const

export type DocumentId = keyof typeof documents

/** All available document templates for the picker UI. */
export const documentList = Object.entries(documents).map(([id, doc]) => ({
  id,
  label: doc.label,
}))

/** Look up a document template by ID. */
export function getDocumentTemplate<T extends DocumentId>(id: T): (typeof documents)[T] {
  return documents[id as keyof typeof documents] as unknown as (typeof documents)[T]
}
